import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User as UserIcon,
  Lock,
  Shield,
  Download,
  Trash2,
  Check,
  AlertTriangle,
  History,
  Mail,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { format, parseISO } from 'date-fns';

const ROLE_LABELS = {
  property_manager: 'Property Manager',
  owner: 'Owner',
  cleaner: 'Cleaner',
  maintenance: 'Maintenance',
  landscaping: 'Landscaping',
  repair: 'Repair',
};

const EVENT_LABELS = {
  login_password: 'Signed in with password',
  login_google: 'Signed in with Google',
  password_change: 'Password changed',
  account_deleted: 'Account deleted',
};

const fmt = (d) => {
  if (!d) return '—';
  try { return format(parseISO(d), "MMM d, yyyy 'at' h:mm a"); } catch { return d; }
};

export default function AccountView() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  const [exporting, setExporting] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || '', email: user.email || '' });
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/account/activity');
        setActivity(data.entries || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingActivity(false);
      }
    })();
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/account/profile', {
        name: profile.name,
        email: user?.is_google_user ? undefined : profile.email,
      });
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (passwordForm.next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/account/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.next,
      });
      setPasswordForm({ current: '', next: '', confirm: '' });
      toast.success('Password changed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get('/account/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `propveiw-account-${user?.id || 'export'}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/account', { data: { confirm_email: deleteConfirmEmail } });
      toast.success('Your account has been deleted. Sorry to see you go.');
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete account');
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8" data-testid="account-view">
      {/* Header */}
      <div>
        <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
          My Account
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your profile, security, and data.
        </p>
      </div>

      {/* Profile */}
      <Card className="p-6" data-testid="account-profile-card">
        <div className="flex items-center gap-3 mb-5">
          <UserIcon className="h-5 w-5 text-slate-500" />
          <h2 className="font-semibold text-lg text-slate-900">Profile</h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="acct-name">Full name</Label>
            <Input
              id="acct-name"
              data-testid="account-name-input"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="acct-email">Email</Label>
            <Input
              id="acct-email"
              data-testid="account-email-input"
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              disabled={user.is_google_user}
              className="mt-1"
            />
            {user.is_google_user && (
              <p className="text-xs text-slate-500 mt-1">
                Managed by Google — change it in your Google account.
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 mt-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Role</Label>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {ROLE_LABELS[user.role] || user.role}
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Account type</Label>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {user.is_google_user ? 'Google-linked' : 'Email + password'}
            </p>
          </div>
          {user.subscription_plan && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Plan</Label>
              <p className="mt-1 text-sm font-medium text-slate-900 capitalize">
                {user.subscription_plan}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={saveProfile}
            disabled={savingProfile}
            data-testid="account-save-profile"
            className="bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white"
          >
            {savingProfile ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </Card>

      {/* Security */}
      <Card className="p-6" data-testid="account-security-card">
        <div className="flex items-center gap-3 mb-5">
          <Lock className="h-5 w-5 text-slate-500" />
          <h2 className="font-semibold text-lg text-slate-900">Security</h2>
        </div>

        {user.is_google_user ? (
          <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
            Your password is managed by Google. Sign in with Google as usual and manage
            your credentials at{' '}
            <a
              href="https://myaccount.google.com/security"
              target="_blank"
              rel="noreferrer"
              className="text-[#0B1E3F] underline"
            >
              myaccount.google.com
            </a>.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="pwd-cur">Current password</Label>
              <Input
                id="pwd-cur"
                data-testid="account-password-current"
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="pwd-new">New password</Label>
              <Input
                id="pwd-new"
                data-testid="account-password-new"
                type="password"
                value={passwordForm.next}
                onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="pwd-cfm">Confirm new password</Label>
              <Input
                id="pwd-cfm"
                data-testid="account-password-confirm"
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-3 flex justify-end">
              <Button
                onClick={changePassword}
                disabled={changingPassword || !passwordForm.current || !passwordForm.next}
                data-testid="account-change-password-btn"
                className="bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white"
              >
                {changingPassword ? 'Updating…' : 'Change password'}
              </Button>
            </div>
          </div>
        )}

        {/* Activity */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
              Recent activity
            </h3>
          </div>
          {loadingActivity ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : activity.length === 0 ? (
            <div className="text-sm text-slate-500">No recent activity recorded.</div>
          ) : (
            <ul className="space-y-2 text-sm" data-testid="account-activity-list">
              {activity.map((e) => (
                <li
                  key={e.id}
                  className="flex items-start justify-between gap-3 py-2 border-b border-slate-50 last:border-0"
                >
                  <div>
                    <p className="font-medium text-slate-900">
                      {EVENT_LABELS[e.event] || e.event}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {e.ip || 'Unknown IP'} · {e.user_agent
                        ? e.user_agent.split(' ')[0] + '…'
                        : 'Unknown browser'}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500 shrink-0">{fmt(e.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Data & Privacy */}
      <Card className="p-6" data-testid="account-privacy-card">
        <div className="flex items-center gap-3 mb-5">
          <Shield className="h-5 w-5 text-slate-500" />
          <h2 className="font-semibold text-lg text-slate-900">Data &amp; Privacy</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 p-4 rounded-md border border-slate-200 bg-slate-50/50">
            <div>
              <p className="font-medium text-slate-900">Download my data</p>
              <p className="text-sm text-slate-500 mt-1">
                A JSON export of your profile, properties, bookings, tasks,
                expenses, invoices, and login history.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={exportData}
              disabled={exporting}
              data-testid="account-export-btn"
              className="shrink-0"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Preparing…' : 'Export'}
            </Button>
          </div>

          <div className="flex items-start justify-between gap-4 p-4 rounded-md border border-red-200 bg-red-50/50">
            <div>
              <p className="font-medium text-red-900">Delete my account</p>
              <p className="text-sm text-red-800/80 mt-1">
                Permanently remove your account. Records you created in your
                organization (bookings, tasks, expenses) stay with the org so
                nothing breaks for teammates.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => { setDeleteConfirmEmail(''); setDeleteOpen(true); }}
              data-testid="account-delete-btn"
              className="shrink-0 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-900"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </Card>

      {/* Billing quick link */}
      <Card className="p-6 flex items-center justify-between" data-testid="account-billing-card">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-slate-500" />
          <div>
            <p className="font-medium text-slate-900">Billing &amp; subscription</p>
            <p className="text-sm text-slate-500">
              Manage plan, invoices, and payment method.
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/billing')}
          variant="outline"
          data-testid="account-billing-link"
        >
          Go to billing
        </Button>
      </Card>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onOpenChange={(v) => !deleting && setDeleteOpen(v)}>
        <DialogContent data-testid="account-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Delete your account?
            </DialogTitle>
            <DialogDescription>
              This cannot be undone. Type <strong>{user.email}</strong> below to
              confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="account-delete-confirm-input"
            placeholder={user.email}
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            disabled={deleting}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteAccount}
              disabled={
                deleting ||
                deleteConfirmEmail.trim().toLowerCase() !== user.email.toLowerCase()
              }
              data-testid="account-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Deleting…' : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Yes, delete my account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Badge className="sr-only" data-testid="account-user-id">{user.id}</Badge>
    </div>
  );
}
